import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { queryManager } from 'ember-apollo-client';
import { dropTask, task } from 'ember-concurrency';
import { TrackedArray } from 'tracked-built-ins';

import { ACCEPTED_ISSUES } from 'my-phorest/utils/calendar';
import { BookingIntent } from 'my-phorest/utils/planner';
import { fetchPolicy } from 'my-phorest/utils/enums/fetch-policy';
import ChainServiceWithServices from 'my-phorest/gql/queries/chain-service-with-services.graphql';
import serviceQuery from 'my-phorest/gql/queries/service.graphql';
import * as calendarCache from 'my-phorest/utils/calendar-cache';

export default class AppointmentPlannerService extends Service {
  @service appointmentSlideOver;
  @service appointmentTasks;
  @service errorHandler;
  @service packageTasks;
  @service router;
  @service session;

  @queryManager apollo;

  @tracked availableTimesForDate = null;
  @tracked bookingIntents = new TrackedArray([]); // services and packages selected to be booked
  @tracked bookingIntentContext;
  @tracked branchId = this.session.branchId;
  @tracked client = null;
  @tracked date = null;
  @tracked packageOptionIntentContext;
  @tracked startTime = null;

  get isPlanningWithPackages() {
    return this.bookingIntents.some((bookingIntent) => bookingIntent.isPackage);
  }

  get isSearchingInOtherBranch() {
    return (
      this.branchId !== this.session.branchId && !this.isPlanningWithPackages
    );
  }

  addBookingIntent(bookingIntent) {
    this.bookingIntents.push(bookingIntent);
    return bookingIntent;
  }

  close() {
    this.router.transitionTo('accounts.account.appointments');
  }

  createBookingIntent(obj) {
    return new BookingIntent(obj, this);
  }

  @task
  *getServiceIdForCourseBookingIntentTask(bookingIntent) {
    const chainService = yield this.apollo.query(
      {
        query: ChainServiceWithServices,
        variables: {
          id: bookingIntent.clientCourseItem.chainService.id,
          fetchServicesPolicy: fetchPolicy.ACTIVE,
        },
      },
      'chainService'
    );
    const branchService = chainService.services.find(
      (s) => s.owningBranchId === this.branchId
    );
    return branchService?.id ?? null;
  }

  async getServiceIdForChainService(service) {
    if (this.isSearchingInOtherBranch) {
      const chainService = await this.apollo.query(
        {
          query: ChainServiceWithServices,
          variables: {
            id: service.parentId,
            fetchServicesPolicy: fetchPolicy.ACTIVE,
          },
        },
        'chainService'
      );
      const branchService = chainService.services.find(
        (s) => s.owningBranchId === this.branchId
      );
      return branchService?.id ?? null;
    } else {
      return service.id;
    }
  }

  openAppointmentSlideOver(appointments) {
    this.appointmentSlideOver.open({
      appointments,
      client: this.client,
      staffMember: this.#getLastStaff(appointments),
      screen: 'appointment-details',
    });
  }

  #getLastStaff(appointments) {
    let appointment = appointments[appointments.length - 1];
    return appointment.staffMember;
  }

  removeBookingIntent(bookingIntent) {
    const index = this.bookingIntents.indexOf(bookingIntent);
    this.bookingIntents.splice(index, 1);
    this.bookingIntentContext = this.bookingIntents[0];
  }

  reset() {
    this.availableTimesForDate = null;
    this.bookingIntents = new TrackedArray([]);
    this.bookingIntentContext = undefined;
    this.branchId = this.session.branchId;
    this.client = null;
    this.date = null;
    this.startTime = null;
  }

  @task
  *createAppointmentsPayload(
    [bookingIntent, ...rest],
    payload = [],
    availableTimesForDate
  ) {
    if (!bookingIntent) {
      return payload;
    }
    const clientId = this.client.id;
    const issues = ACCEPTED_ISSUES;
    const date = this.date;

    const variables = {
      clientId,
      date,
      issues,
    };

    if (bookingIntent.isService) {
      variables.serviceId = yield this.getServiceIdForChainService(
        bookingIntent.service
      );
    }
    if (bookingIntent.isCourse) {
      variables.serviceId =
        yield this.getServiceIdForCourseBookingIntentTask.perform(
          bookingIntent
        );
      variables.clientCourseItemId = bookingIntent.clientCourseItem.id;
    }

    if (this.isSearchingInOtherBranch) {
      variables.branchId = this.branchId;
    }

    if (bookingIntent.staffRequested) {
      variables.staffRequested = bookingIntent.staffRequested;
    }

    let { nextAvailableTimesForDate, input, acceptedIssues } =
      bookingIntent.getGqlVariables(availableTimesForDate, variables);

    Object.assign(variables, {
      input,
      acceptedIssues,
    });

    availableTimesForDate = nextAvailableTimesForDate;

    payload.push({ variables, bookingIntent });
    return this.createAppointmentsPayload.perform(
      rest,
      payload,
      availableTimesForDate
    );
  }

  @dropTask
  *createAppointmentsTask() {
    let createdAppointments = [];

    let payload = yield this.createAppointmentsPayload.perform(
      this.bookingIntents,
      [],
      this.availableTimesForDate
    );
    for (const appointment of payload) {
      let response =
        yield this.appointmentTasks.createAppointmentsInSequenceTask.perform(
          appointment.variables
        );
      if (response.appointments) {
        calendarCache.addEventsToCalendarCache(
          this.apollo.apollo.client.cache,
          response.appointments
        );
        createdAppointments.push(...response.appointments);
      }
    }
    return createdAppointments;
  }

  @dropTask
  *reloadBookingIntentTask(bookingIntent) {
    if (bookingIntent.isPackage) {
      return yield this.reloadPackageTask.perform(bookingIntent);
    } else if (bookingIntent.isCourse) {
      return yield this.reloadClientCourseTask.perform(bookingIntent);
    } else {
      return yield this.reloadServiceTask.perform(bookingIntent);
    }
  }

  @dropTask
  *reloadPackageTask(bookingIntent) {
    try {
      let staffMemberContexts = bookingIntent.allStaffMemberContexts;
      const packageObj = yield this.packageTasks.fetchPackageTask
        .linked()
        .perform(bookingIntent.selectedPackage.id, staffMemberContexts);

      packageObj.serviceGroupOptions.forEach((optionGroup, index) => {
        const selectedPackageOption =
          bookingIntent.selectedPackageOptions[index];
        const newOption = optionGroup.find(
          (option) =>
            option.id === selectedPackageOption.serviceGroupItemOptionId
        );

        selectedPackageOption.option = newOption;
      });
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }

  @dropTask
  *reloadServiceTask(bookingIntent) {
    try {
      let variables = { id: bookingIntent.service.id };

      if (bookingIntent.staffMember) {
        variables = {
          ...variables,
          servicePriceContext: {
            staffCategoryId: bookingIntent.staffMember.categoryId,
          },
          serviceDurationContext: {
            userId: bookingIntent.staffMember.userId,
          },
        };
      }

      bookingIntent.service = yield this.apollo.query(
        {
          query: serviceQuery,
          variables,
        },
        'service'
      );
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }

  @dropTask
  *reloadClientCourseTask(bookingIntent) {
    try {
      let serviceId =
        yield this.getServiceIdForCourseBookingIntentTask.perform(
          bookingIntent
        );
      let variables = { id: serviceId };

      if (bookingIntent.staffMember) {
        variables = {
          ...variables,
          servicePriceContext: {
            staffCategoryId: bookingIntent.staffMember.categoryId,
          },
          serviceDurationContext: {
            userId: bookingIntent.staffMember.userId,
          },
        };
      }
      bookingIntent.service = yield this.apollo.query(
        {
          query: serviceQuery,
          variables,
        },
        'service'
      );
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }
}
